import { DrawerLayout, HStack } from '@cad-arc';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import { EventSearchWindow } from '../event-search/event-search-window';
import { WorkspacePanels } from '../panels/workspace-panels';
import { UnitStatusSearchWindow } from '../unit-status-search/unit-status-search-window';
import { UnitStatusSearch } from '../unit-status-search/fr-unit-status-search/unit-status-search';
import { MapWindowElement } from '../map-window/components/map-window-element';
import {
    EVENTS_WINDOW_PATH,
    EVENT_SEARCH_WINDOW_PATH,
    QUEUES_WINDOW_PATH,
    UNIT_STATUS_SEARCH_WINDOW_PATH,
    FR_UNIT_HISTORY_PATH,
    MAP_WINDOW_PATH,
    FR_DEX_WINDOW_PATH,
    FR_EVENTS_LIST_PATH,
    FR_EDIT_UNIT_PATH,
    ROOT_PATH,
    FR_EVENT_DETAILS_SUB_PATH,
    FR_EDIT_EVENT_SUB_PATH,
    FR_CREATE_EVENT_SUB_PATH,
    FR_UNASSIGN_CURRENT_USER_UNIT_SUB_PATH,
    FR_CLEAR_EVENT_SUB_PATH,
    FR_EDIT_DISPOSITION_SUB_PATH,
    BOLO_SEARCH_WINDOW_PATH,
    FR_ADD_EVENT_PROPERTY_SUB_PATH,
    FR_EVENT_ATTACHMENTS_SUB_PATH,
    FR_UNITS_LIST_PATH,
    FR_BOLO_ROOT_PATH,
    FR_BOLO_ATTACHMENTS_SUB_PATH,
    FR_CONTACT_LIST_PATH,
    FR_EVENT_ROOT_PATH,
} from './constants';
import { UnitHistory as UnitHistoryPage } from '../unit-history';
import { FrHomepage } from '../fr-homepage/fr-homepage';
import { FrDexPage } from '../fr-dex-page/fr-dex-page-wrapper';
import { EventSearch } from '../event-search/fr-event-search/event-search';
import { FrEventsListPage } from '../fr-events-list-page/fr-events-list-page';
import { FrEventDetailsPage } from '../fr-event-details-page';
import { FrEditUnitPage } from '../fr-edit-unit-page/fr-edit-unit-page';
import { FrCreateOrEditEventPage } from '../fr-create-or-edit-event-page/fr-create-or-edit-event-page';
import { BoloSearchWindow } from '../bolos/bolo-search/bolo-search-window';
import { FrClearEventPage } from '../fr-clear-event-page/fr-clear-event-page';
import { FrEditDispositionPage } from '../fr-edit-disposition-page/fr-edit-disposition-page';
import { FrUnassignCurrentUserUnitPage } from '../fr-unassign-current-user-unit-page/fr-unassign-current-user-unit-page';
import { FrAddEventPropertyPage } from '../fr-add-event-property-page/fr-add-event-property-page';
import { FrUnitsListPage } from '../fr-units-list/fr-units-list-page/fr-units-list-page';
import { FrContactListPage } from '../fr-contact-list-page/fr-contact-list-page';
import { FrBoloPage } from '../fr-bolos-page';
import { FrEventRootProviders } from '../fr-event-providers/fr-event-root-providers';
import { FrBoloRootProviders } from '../fr-bolo-providers/fr-bolo-root-providers';
import { FrAssignedEventPage } from '../fr-assigned-event-page';
import { FrEventAttachmentsPage } from '../fr-event-attachments-page';
import { FrBoloAttachmentsPage } from '../fr-bolo-attachments-page';

function EventsElement(): React.ReactElement {
    return (
        <DrawerLayout>
            <HStack spacing={0} w="100%" h="100%">
                <div
                    id="inline-drawer-portal-id"
                    style={{
                        height: '100%',
                        position: 'relative',
                    }}
                ></div>
                <WorkspacePanels />
            </HStack>
        </DrawerLayout>
    );
}

export function createFrRoutes(options: {
    providerElement: React.ReactElement;
}): Parameters<typeof createBrowserRouter>[0] {
    return [
        {
            element: options.providerElement,
            children: [
                {
                    path: ROOT_PATH,
                    element: <FrHomepage />,
                },
                {
                    path: FR_EVENT_ROOT_PATH,
                    element: <FrEventRootProviders />,
                    children: [
                        {
                            path: FR_CREATE_EVENT_SUB_PATH,
                            element: <FrCreateOrEditEventPage />,
                        },
                        {
                            path: ':eventId',
                            element: <FrAssignedEventPage />,
                        },
                        {
                            path: `:eventId/${FR_EDIT_EVENT_SUB_PATH}`,
                            element: <FrCreateOrEditEventPage />,
                        },
                        {
                            path: `:eventId/${FR_EVENT_DETAILS_SUB_PATH}`,
                            element: <FrEventDetailsPage />,
                        },
                        {
                            path: `:eventId/${FR_ADD_EVENT_PROPERTY_SUB_PATH}`,
                            element: <FrAddEventPropertyPage />,
                        },
                        {
                            path: `:eventId/${FR_CLEAR_EVENT_SUB_PATH}`,
                            element: <FrClearEventPage />,
                        },
                        {
                            path: `:eventId/${FR_EDIT_DISPOSITION_SUB_PATH}`,
                            element: <FrEditDispositionPage />,
                        },
                        {
                            path: `:eventId/${FR_UNASSIGN_CURRENT_USER_UNIT_SUB_PATH}`,
                            element: <FrUnassignCurrentUserUnitPage />,
                        },
                        {
                            path: `:eventId/${FR_EVENT_ATTACHMENTS_SUB_PATH}`,
                            element: <FrEventAttachmentsPage />,
                        },
                        {
                            path: `:eventId/${FR_EVENT_ATTACHMENTS_SUB_PATH}/:attachmentId`,
                            element: <FrEventAttachmentsPage />,
                        },
                    ],
                },
                {
                    path: FR_BOLO_ROOT_PATH,
                    element: <FrBoloRootProviders />,
                    children: [
                        {
                            path: ':boloId',
                            element: <FrBoloPage />,
                        },
                        {
                            path: `:boloId/${FR_BOLO_ATTACHMENTS_SUB_PATH}`,
                            element: <FrBoloAttachmentsPage />,
                        },
                        {
                            path: `:boloId/${FR_BOLO_ATTACHMENTS_SUB_PATH}/:attachmentId`,
                            element: <FrBoloAttachmentsPage />,
                        },
                    ],
                },
                {
                    path: MAP_WINDOW_PATH,
                },
                {
                    path: EVENT_SEARCH_WINDOW_PATH,
                    element: <EventSearch />,
                },
                {
                    path: FR_UNIT_HISTORY_PATH,
                    element: <UnitHistoryPage />,
                },
                {
                    path: UNIT_STATUS_SEARCH_WINDOW_PATH,
                    element: <UnitStatusSearch />,
                },
                {
                    path: FR_DEX_WINDOW_PATH,
                    element: <FrDexPage />,
                },
                {
                    path: FR_EVENTS_LIST_PATH,
                    element: <FrEventsListPage />,
                },
                {
                    path: FR_UNITS_LIST_PATH,
                    element: <FrUnitsListPage />,
                },
                {
                    path: FR_EDIT_UNIT_PATH,
                    element: <FrEditUnitPage />,
                },
                {
                    path: FR_CONTACT_LIST_PATH,
                    element: <FrContactListPage />,
                },
                {
                    path: '*',
                    element: <Navigate to={ROOT_PATH} />,
                },
            ],
        },
    ];
}

export function createCadRoutes(options: {
    providerElement: React.ReactElement;
}): Parameters<typeof createBrowserRouter>[0] {
    return [
        {
            element: options.providerElement,
            children: [
                {
                    path: QUEUES_WINDOW_PATH,
                    element: <WorkspacePanels />,
                },
                {
                    path: EVENTS_WINDOW_PATH,
                    element: <EventsElement />,
                },
                {
                    path: MAP_WINDOW_PATH,
                    element: <MapWindowElement />,
                },
                {
                    path: EVENT_SEARCH_WINDOW_PATH,
                    element: <EventSearchWindow />,
                },
                {
                    path: UNIT_STATUS_SEARCH_WINDOW_PATH,
                    element: <UnitStatusSearchWindow />,
                },
                {
                    path: BOLO_SEARCH_WINDOW_PATH,
                    element: <BoloSearchWindow />,
                },
                {
                    path: '*',
                    element: <Navigate to={QUEUES_WINDOW_PATH} />,
                },
            ],
        },
    ];
}
